import { ForwardedRef, forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { Modal, Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { Select, SelectStyle } from 'src/common/components/Select';
import { useQuery } from 'src/common/hooks/useQuery';

import {
  createAlarmRule,
  deleteAlarmRule,
  fetchAlarmRules,
  fetchSlackChannels,
  updateAlarmRule,
} from '../api';
import { ALARM_EVENT_TYPES, ApiAccountType, AlarmRuleType, SlackChannelType } from '../types';

export const AlarmRuleModal = forwardRef((_, ref: ForwardedRef<ModalRefProps>): JSX.Element => {
  const modalRef = useRef<ModalRefProps>(null);
  const [account, setAccount] = useState<ApiAccountType | null>(null);
  const [eventType, setEventType] = useState<string>(ALARM_EVENT_TYPES[0].value);
  const [channel, setChannel] = useState<SlackChannelType | null>(null);

  // SWR is keyed, not fetcher-gated: the key itself must change for SWR to
  // refetch. This flips once the modal has been opened with an account
  // (channels are global, not per-account, so there's no need to key on
  // `account.id` and refetch on every subsequent open).
  const { data: channelsData } = useQuery(`/slack_channels?_open=${account ? '1' : '0'}`, () =>
    account ? fetchSlackChannels() : Promise.resolve({ data: [] })
  );
  const channels = (channelsData as SlackChannelType[]) || [];

  const {
    data: rulesData,
    isLoading,
    mutate,
  } = useQuery(`/account_alarm_rules?account=${account?.id || ''}`, () =>
    account ? fetchAlarmRules(account.id) : Promise.resolve({ data: { results: [] } })
  );
  const rules = (rulesData as { results: AlarmRuleType[] })?.results || [];

  useImperativeHandle(ref, () => ({
    show: (item: ApiAccountType) => {
      setAccount(item);
      setChannel(null);
      setEventType(ALARM_EVENT_TYPES[0].value);
      modalRef.current?.show();
    },
    hide: () => modalRef.current?.hide(),
  }));

  const handleAddRule = async (): Promise<void> => {
    if (!account || !channel) return;
    try {
      await createAlarmRule({
        account: account.id,
        event_type: eventType,
        slack_channel_id: channel.id,
        slack_channel_name: channel.name,
      });
      Toast.success('Alarm rule created.');
      setChannel(null);
      await mutate();
    } catch (error) {
      Toast.error('Error creating alarm rule.');
    }
  };

  const handleToggleRule = async (rule: AlarmRuleType): Promise<void> => {
    try {
      await updateAlarmRule(rule.id, { enabled: !rule.enabled });
      await mutate();
    } catch (error) {
      Toast.error('Error updating alarm rule.');
    }
  };

  const handleDeleteRule = async (rule: AlarmRuleType): Promise<void> => {
    try {
      await deleteAlarmRule(rule.id);
      Toast.success('Alarm rule deleted.');
      await mutate();
    } catch (error) {
      Toast.error('Error deleting alarm rule.');
    }
  };

  return (
    <Modal
      ref={modalRef}
      actionText="Close"
      cancelText="Close"
      showAction={false}
      actionFunc={() => {}}
    >
      <div className="w-96">
        <h3>Alarms for {account?.name}</h3>

        <ul className="mt-4">
          {!isLoading &&
            rules.map((rule) => (
              <li key={rule.id} className="flex items-center justify-between py-2 border-b">
                <div>
                  <div>{ALARM_EVENT_TYPES.find((e) => e.value === rule.event_type)?.label}</div>
                  <div className="text-xs text-gray-2.0">#{rule.slack_channel_name}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn bg-white-2.0"
                    onClick={() => handleToggleRule(rule)}
                  >
                    {rule.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    className="btn bg-white-2.0"
                    aria-label="Delete alarm rule"
                    onClick={() => handleDeleteRule(rule)}
                  >
                    <Icon name="trash" />
                  </button>
                </div>
              </li>
            ))}
        </ul>

        <div className="mt-4">
          <h4>Add alarm</h4>
          <div className="mt-2">
            <Select
              placeholder="Event"
              options={ALARM_EVENT_TYPES}
              fieldId="value"
              fieldLabel="label"
              value={eventType}
              onChange={(option) => setEventType((option as { value: string }).value)}
              styles={SelectStyle}
              menuPortalTarget={document.body}
            />
          </div>
          <div className="mt-2">
            <Select
              placeholder="Slack channel"
              options={channels}
              fieldId="id"
              fieldLabel="name"
              value={channel?.id}
              onChange={(option) => setChannel(option as SlackChannelType | null)}
              isClearable
              styles={SelectStyle}
              menuPortalTarget={document.body}
            />
          </div>
          <button
            type="button"
            className="btn btn-secondary mt-4"
            disabled={!channel}
            onClick={handleAddRule}
          >
            Add alarm
          </button>
        </div>
      </div>
    </Modal>
  );
});
